import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contentName", "professorId", "useCases", "hsSoftwareFilters", "subCategory"];
import findIndex from 'hs-lodash/findIndex';
import maxBy from 'hs-lodash/maxBy';
import { useCallback, useEffect, useMemo } from 'react';
import { Redirect, useHistory, useParams } from 'quartz-routing/react-router';
import { NavMarker } from 'react-rhumb';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { mapLessonContentTypenameToType, unwrapLessonChirpError } from 'academy-common-lib/features/lessons/lessonUtils';
import { chirpEnumToString } from 'academy-common-lib/utils/chirp';
import { VideoAssetTypename } from '../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import getLang from 'LearningCenterUI/utils/getLang';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useLessonProgressById, usePlaylistProgress } from '../features/userProgress';
import { PRE_ASSESSMENT_URL_FRAGMENT, TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS } from '../constants/Navigation';
import { HasParentPlaylist, NextPlaylistContent, PLAYLIST_SEARCH_PARAM } from '../features/playlistContent';
import useWootricSurvey from '../hooks/useWootricSurvey';
import LessonNavigator from '../components/Navigator/LessonNavigator';
import ACNavigatorButton from 'academy-common-lib/components/navigation/contentNavigator/ACNavigatorButton';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useSetLastClickedRecommendationItemFromUrl } from '../features/recommendations/recommendationsHooks';
import { useSingleLanguageRedirect } from '../hooks/useSingleLangaugeRedirect';
import { maybeLessonCardModelWithProgress } from '../features/lessons/lessonUtils';
import { useFullPlaylist } from '../features/savedItems/savedItemsQueries';
import { useFullLessonChirp } from '../features/lessons/lessonQueries';
import UsageTracker from '../utils/UsageTracker';
import { SCREENS } from '../constants/Tracking';
import ApiError from '../components/Errors/ApiError';
import LearningContentInnerWrapper from '../features/learningContent/components/LearningContentInnerWrapper';
import LearningPageHeader from '../containers/TrackPage/LearningPageHeader';
import LearningPageSidebar from '../containers/TrackPage/LearningPageSidebar';
import LearningPageMainSection from '../containers/TrackPage/LearningPageMainSection';
import LessonPage from '../components/LessonPage/LessonPage';
// @ts-expect-error Could not find declaration file for module
import { useSearchParam } from '../hooks/searchParamHooks';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { FILTER_TYPES } from '../constants/TopFilterConstants';
import { useLessonPreQuizSession } from '../features/preAssessmentQuiz/hooks/useLessonPreQuiz';
import { getFocusContentId, isPreAssessmentCompleted } from '../features/preAssessmentQuiz/utils/preAssessmentQuizUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noRedirectRoutes = [TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS, PRE_ASSESSMENT_URL_FRAGMENT];
export const useLessonRedirectUrl = ({
  fullLesson,
  contentId,
  content,
  lessonProgressById,
  playlistId,
  playlistProgress
}) => {
  const {
    location
  } = useHistory();
  const lessonRedirectUrl = useMemo(() => {
    if (!lessonProgressById || !fullLesson) return null;

    // We never want to redirect from the pages shown at the end of a lesson
    for (const route of noRedirectRoutes) {
      if (location.pathname.includes(route)) return null;
    }
    if (contentId) {
      if (!content) return `/lessons/${fullLesson.id}`;else return null;
    }
    const allContentProgress = [];
    fullLesson.content.forEach(currentContent => {
      const contentProgress = lessonProgressById.contentProgress.find(c => c.contentId === currentContent.id);
      allContentProgress.push({
        contentId: currentContent.id,
        status: contentProgress ? contentProgress.progressType : 'NOT_STARTED',
        updatedAt: contentProgress ? contentProgress.updatedAt : null,
        url: `/lessons/${fullLesson.id}/${currentContent.id}`
      });
    });
    let finalUrl;
    if (playlistId) {
      if (playlistProgress && playlistProgress.status !== 'COMPLETED') {
        finalUrl = `/lessons/${fullLesson.id}/${TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT}`;
      } else {
        finalUrl = allContentProgress[0].url;
      }
    } else {
      finalUrl = `/lessons/${fullLesson.id}/${TRACK_RECOMMENDATIONS_URL_FRAGMENT}`;
    }
    allContentProgress.push({
      contentId: null,
      status: null,
      updatedAt: null,
      url: finalUrl
    });
    if (lessonProgressById.status === 'COMPLETED') {
      return allContentProgress[allContentProgress.length - 1].url;
    }
    const lastCompleted = maxBy(allContentProgress.filter(p => p.status === 'COMPLETED'), p => p.updatedAt);
    if (lastCompleted) {
      const lastCompletedIndex = findIndex(allContentProgress, lastCompleted);
      const unprogressedItemsAfterLastCompletedItem = allContentProgress.slice(lastCompletedIndex + 1).filter(p => p.status !== 'COMPLETED');
      return unprogressedItemsAfterLastCompletedItem[0].url;
    }
    return allContentProgress[0].url;
  }, [content, contentId, fullLesson, lessonProgressById, location.pathname, playlistId, playlistProgress]);
  return lessonRedirectUrl;
};
const getNextContentId = (fullLesson, contentId) => {
  var _fullLesson$content;
  if (!fullLesson || !fullLesson.content || !contentId) {
    return undefined;
  }
  const currentIndex = fullLesson.content.findIndex(c => c.id === contentId);
  return (_fullLesson$content = fullLesson.content[currentIndex + 1]) === null || _fullLesson$content === void 0 ? void 0 : _fullLesson$content.id;
};
const useLessonPage = () => {
  const {
    contentId: contentIdParam,
    lessonId: lessonIdParam
  } = useParams();
  const [[playlistIdParam]] = useSearchParam(PLAYLIST_SEARCH_PARAM);
  const [[languageParam]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const contentId = Number(contentIdParam);
  const lessonId = Number(lessonIdParam);
  const languageOrDefault = languageParam || getLang();
  const playlistId = Number(playlistIdParam);
  const {
    lesson: fullLesson,
    lessonLoading: fullLessonLoading,
    lessonError: fullLessonError
  } = useFullLessonChirp({
    lessonId
  });
  const {
    playlist
  } = useFullPlaylist(playlistId, !playlistId);
  const {
    playlistProgress
  } = usePlaylistProgress(playlistId, !playlistId);
  const {
    lessonProgressById,
    lessonProgressByIdError
  } = useLessonProgressById(lessonId);
  const lessonCardModel = maybeLessonCardModelWithProgress({
    lesson: fullLesson,
    progress: lessonProgressById
  });
  const content = fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.content.find(c => c.id === contentId);
  const contentFragment = isNaN(contentId) ? contentIdParam : undefined;
  const contentProgress = lessonProgressById === null || lessonProgressById === void 0 ? void 0 : lessonProgressById.contentProgress.find(p => p.contentId === contentId);
  return {
    lessonId,
    language: languageOrDefault,
    fullLesson,
    lessonCardModel,
    contentId,
    contentFragment,
    content,
    contentProgress,
    lessonProgressById,
    lessonProgressByIdError,
    playlistId,
    playlist,
    playlistProgress,
    isLoading: fullLessonLoading,
    error: unwrapLessonChirpError(fullLessonError)
  };
};
const LessonPageRouter = () => {
  const {
    isTablet,
    isNotLaptop
  } = useMediaQuery();
  useWootricSurvey('CSAT');
  const {
    push,
    location
  } = useHistory();
  const {
    lessonId,
    language,
    fullLesson,
    lessonCardModel,
    contentId,
    content,
    contentFragment,
    contentProgress,
    lessonProgressById,
    playlistId,
    playlist,
    playlistProgress,
    error,
    isLoading
  } = useLessonPage();
  const contentType = content && mapLessonContentTypenameToType(content);
  const lessonRedirectUrl = useLessonRedirectUrl({
    fullLesson,
    contentId,
    content,
    lessonProgressById: lessonProgressById || undefined,
    playlistId,
    playlistProgress: playlistProgress || undefined
  });
  const {
    redirectUrl: singleLanguageRedirectUrl,
    loading: singleLanguageRedirectLoading
  } = useSingleLanguageRedirect({
    contentId: lessonId,
    language,
    contentType: 'LESSON'
  });
  useSetLastClickedRecommendationItemFromUrl({
    id: lessonId ? Number(lessonId) : undefined,
    type: 'LESSON'
  });
  const nextContentId = getNextContentId(fullLesson, contentId);
  const {
    getLessonPreQuizState
  } = useLessonPreQuizSession();
  const preQuizState = getLessonPreQuizState(lessonId);
  const handleAdvanceContent = useCallback(() => {
    if (fullLesson && preQuizState && isPreAssessmentCompleted(preQuizState)) {
      const nextFocusId = getFocusContentId({
        currentContentId: contentId,
        lessonContent: fullLesson.content,
        perContentScores: preQuizState.perContentScores
      });
      if (nextFocusId) {
        push(Object.assign({}, location, {
          pathname: `/lessons/${lessonId}/${nextFocusId}`
        }));
        return;
      }
    }
    if (nextContentId) {
      push(Object.assign({}, location, {
        pathname: `/lessons/${lessonId}/${nextContentId}`
      }));
    } else {
      push(Object.assign({}, location, {
        pathname: `recommendations`
      }));
    }
  }, [nextContentId, push, location, lessonId, fullLesson, contentId, preQuizState]);
  const handleSkipPreAssessment = useCallback(() => push(Object.assign({}, location, {
    pathname: `/lessons/${lessonId}`
  })), [push, location, lessonId]);
  const handleStartLessonWithPreAssessmentFocus = useCallback(() => {
    if (fullLesson && preQuizState && isPreAssessmentCompleted(preQuizState)) {
      const firstFocusId = getFocusContentId({
        lessonContent: fullLesson.content,
        perContentScores: preQuizState.perContentScores
      });
      if (firstFocusId) {
        push(Object.assign({}, location, {
          pathname: `/lessons/${lessonId}/${firstFocusId}`
        }));
        return;
      }
    }
    push(Object.assign({}, location, {
      pathname: `/lessons/${lessonId}`
    }));
  }, [push, location, lessonId, fullLesson, preQuizState]);
  const trackResourceClick = useCallback(({
    title,
    url
  }) => UsageTracker.trackStandalone('Content Interaction', {
    action: 'open-resource-link',
    contentId,
    contentLanguage: language,
    contentType,
    lessonId,
    lessonName: fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.name,
    resourceTitle: title,
    resourceUrl: url,
    visibility: chirpEnumToString(fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.visibility)
  }), [contentId, contentType, fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.name, fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.visibility, language, lessonId]);
  useEffect(() => {
    if (!fullLesson) {
      return;
    }
    const hsSoftwareFilters = fullLesson.hsSoftwareFilters.map(readUnhandledEnumCase).join(', ');
    const allCategories = fullLesson.subCategories.map(readUnhandledEnumCase).join(', ');
    UsageTracker.trackStandalone('Pageview', {
      screen: SCREENS.LESSON,
      contentId,
      contentType,
      lessonId,
      lessonName: fullLesson.name,
      hsSoftwareFilters,
      allCategories
    });
  }, [contentId, fullLesson, lessonId, contentType]);
  const {
    currentTrackingProps
  } = useMemo(() => {
    if (!fullLesson) {
      return {
        currentFullContent: undefined,
        currentTrackingProps: undefined,
        currentVideoId: undefined,
        currentVideoHublet: undefined,
        currentVideoCreatorPortalId: undefined
      };
    }
    if (content) {
      const {
        professorId: __professorId,
        currentVideoId: __currentVideoId,
        currentVideoHublet: __currentVideoHublet,
        currentVideoCreatorPortalId: __currentVideoCreatorPortalId
      } = content.__typename === VideoAssetTypename ? {
        professorId: content.professors.map(p => p.id),
        currentVideoId: Number(content.reference),
        currentVideoHublet: content.hublet,
        currentVideoCreatorPortalId: content.hubId
      } : {
        professorId: [],
        currentVideoId: undefined,
        currentVideoHublet: undefined,
        currentVideoCreatorPortalId: undefined
      };
      return {
        currentFullContent: content,
        currentTrackingProps: {
          contentName: content.name,
          professorId: __professorId
        },
        currentVideoId: __currentVideoId,
        currentVideoHublet: __currentVideoHublet,
        currentVideoCreatorPortalId: __currentVideoCreatorPortalId
      };
    }
    return {
      currentFullContent: undefined,
      currentTrackingProps: undefined,
      currentVideoId: undefined,
      currentVideoHublet: undefined,
      currentVideoCreatorPortalId: undefined
    };
  }, [fullLesson, content]);
  const {
    instructorIds
  } = useMemo(() => {
    if (!fullLesson) {
      return {
        instructors: [],
        instructorIds: []
      };
    }
    const instructors = fullLesson.content.reduce((acc, curr) => {
      if (curr.__typename === VideoAssetTypename) {
        curr.professors.forEach(instructor => {
          acc[instructor.id] = instructor;
        });
      }
      return acc;
    }, {});
    return {
      instructorIds: Object.keys(instructors).map(Number)
    };
  }, [fullLesson]);
  const {
    aiAccessControls,
    loading: aiAccessControlsLoading
  } = useGetAiAccessControls();
  const aiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);

  // TODO: InteractiveEducation Tracking Disabled Until Release
  const interactiveEducationEnabled = isNotLaptop && aiEnabled;
  const lessonTrackingData = useMemo(() => fullLesson && {
    contentLanguage: fullLesson.language,
    contentName: fullLesson.name,
    creatorHubId: fullLesson.hubId,
    hsSoftwareFilters: fullLesson.hsSoftwareFilters,
    professorId: instructorIds,
    subCategory: fullLesson.subCategories,
    useCases: fullLesson.useCases
  }, [fullLesson, instructorIds]);
  useEffect(() => {
    if (fullLesson && lessonTrackingData && !aiAccessControlsLoading) {
      const {
          hsSoftwareFilters: __hsSoftwareFilters,
          subCategory: __subCategory
        } = lessonTrackingData,
        restLessonTrackingData = _objectWithoutPropertiesLoose(lessonTrackingData, _excluded);
      const feature = interactiveEducationEnabled ? ['AI-QUIZ-ME', 'AI-SUMMARIZATION', 'AI-ADAPT-TO-INDUSTRY'] : [];
      const allCategories = fullLesson.subCategories.map(readUnhandledEnumCase);
      const useCases = fullLesson.useCases.map(readUnhandledEnumCase);
      const businessObjectives = fullLesson.businessObjectives.map(readUnhandledEnumCase);
      const hsSoftwareFilters = __hsSoftwareFilters.map(readUnhandledEnumCase);
      const subCategory = __subCategory.map(readUnhandledEnumCase);
      UsageTracker.trackStandalone('Pageview', Object.assign({
        screen: SCREENS.LESSON,
        contentId,
        contentType,
        feature,
        lessonId,
        lessonName: fullLesson.name,
        allCategories,
        useCases,
        businessObjectives,
        hsSoftwareFilters,
        subCategory
      }, restLessonTrackingData, currentTrackingProps));
    }
  }, [contentId, lessonId, lessonTrackingData, currentTrackingProps, fullLesson, aiAccessControlsLoading, interactiveEducationEnabled, contentType]);
  const assetProgress = useMemo(() => {
    if (!lessonProgressById || !lessonProgressById.contentProgress) {
      return {};
    }
    const progressMap = {};
    lessonProgressById.contentProgress.forEach(currentContentProgress => {
      progressMap[currentContentProgress.contentId] = currentContentProgress.progressType;
    });
    return progressMap;
  }, [lessonProgressById]);
  if (singleLanguageRedirectUrl) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: {
        pathname: singleLanguageRedirectUrl,
        search: window.location.search
      }
    });
  }
  if (!singleLanguageRedirectLoading && error) {
    return /*#__PURE__*/_jsx(ApiError, {
      contentType: "LESSON",
      error: error,
      marker403: "LESSON_VIEW_API_403",
      marker404: "LESSON_VIEW_API_404",
      markerDefault: "LESSON_VIEW_API_ERROR"
    });
  }
  const navigator = fullLesson && /*#__PURE__*/_jsxs(_Fragment, {
    children: [lessonCardModel && /*#__PURE__*/_jsx(LessonNavigator, {
      active: true,
      activeAssetId: contentId,
      assets: fullLesson.content.map(asset => Object.assign({}, asset, {
        completed: assetProgress[asset.id] === 'COMPLETED'
      })),
      chirpLesson: fullLesson,
      lesson: lessonCardModel,
      screen: SCREENS.LESSON
    }), /*#__PURE__*/_jsx(HasParentPlaylist, {
      fallback: /*#__PURE__*/_jsx(ACNavigatorButton, {
        active: contentFragment === TRACK_RECOMMENDATIONS_URL_FRAGMENT,
        helpText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.subheading"
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.heading"
        }),
        to: `/lessons/${lessonId}/${TRACK_RECOMMENDATIONS_URL_FRAGMENT}`
      }),
      children: /*#__PURE__*/_jsx(NextPlaylistContent, {
        lessonId: lessonId,
        render: ({
          title,
          subtitle,
          url,
          urlFragments,
          showPlaylistNavigatorCta
        }) => showPlaylistNavigatorCta ? /*#__PURE__*/_jsx(ACNavigatorButton, {
          active: urlFragments.includes(contentFragment),
          helpText: subtitle,
          title: title,
          to: url
        }) : null
      })
    })]
  });
  return /*#__PURE__*/_jsx(LearningContentInnerWrapper, {
    backgroundColor: "fill-surface-recessed",
    children: /*#__PURE__*/_jsxs("div", {
      children: [!isLoading && lessonCardModel && /*#__PURE__*/_jsx(View, {
        paddingBlock: "600",
        children: /*#__PURE__*/_jsx(LearningPageHeader, {
          ContentName: contentType === 'QUIZ' ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.lessonNavigator.quiz"
          }) : content === null || content === void 0 ? void 0 : content.name,
          additionalTrackingData: lessonTrackingData,
          availableLanguages: fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.availableLanguages,
          defaultBackLink: {
            pathname: '/',
            search: ''
          },
          item: lessonCardModel,
          languageLinkedContent: (fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.languageLinkedContent) || undefined,
          playlist: playlist || undefined,
          playlistProgress: playlistProgress || undefined
        })
      }), isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 200
      }) : /*#__PURE__*/_jsx(NavMarker, {
        name: "LESSON_VIEW_LOADED",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "stretch",
          flexDirection: isTablet ? 'column-reverse' : 'row',
          gap: "600",
          children: [isTablet ? /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-surface-default",
            borderColor: "border-core-subtle",
            borderRadius: "300",
            borderStyle: "solid",
            borderWidth: "100",
            overflow: "hidden",
            children: navigator
          }) : /*#__PURE__*/_jsx(LearningPageSidebar, {
            backgroundColor: "fill-surface-default",
            id: "sidebarNavigator",
            withSubNav: 1,
            children: navigator
          }), /*#__PURE__*/_jsx(LearningPageMainSection, {
            viewProps: {
              minInlineSize: '0'
            },
            children: /*#__PURE__*/_jsx(HasParentPlaylist, {
              fallback: fullLesson !== null && fullLesson !== void 0 && fullLesson.inactiveRedirectUrl || lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
                to: {
                  pathname: (fullLesson === null || fullLesson === void 0 ? void 0 : fullLesson.inactiveRedirectUrl) || lessonRedirectUrl,
                  search: window.location.search
                }
              }) : fullLesson ? /*#__PURE__*/_jsx(LessonPage, {
                content: content,
                contentFragment: contentFragment,
                contentProgress: contentProgress,
                lesson: fullLesson,
                lessonProgress: lessonProgressById || undefined,
                onAdvanceContent: handleAdvanceContent,
                onResourceClick: trackResourceClick,
                onSkipPreAssessment: handleSkipPreAssessment,
                onStartLessonWithPreAssessmentFocus: handleStartLessonWithPreAssessmentFocus
              }) : /*#__PURE__*/_jsx(NavMarker, {
                name: "LESSON_VIEW_CONTENT_MISSING_ERROR",
                children: /*#__PURE__*/_jsx(ACMissingContentError, {})
              }),
              children: /*#__PURE__*/_jsx(NextPlaylistContent, {
                lessonId: lessonId,
                redirectUrl: lessonRedirectUrl || undefined,
                render: ({
                  navigate
                }) => !isLoading && lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
                  to: Object.assign({}, location, {
                    pathname: lessonRedirectUrl
                  })
                }) : fullLesson ? /*#__PURE__*/_jsx(LessonPage, {
                  content: content,
                  contentFragment: contentFragment,
                  contentProgress: contentProgress,
                  lesson: fullLesson,
                  lessonProgress: lessonProgressById || undefined,
                  onAdvanceContent: nextContentId ? handleAdvanceContent : navigate,
                  onResourceClick: trackResourceClick,
                  onSkipPreAssessment: handleSkipPreAssessment,
                  onStartLessonWithPreAssessmentFocus: handleStartLessonWithPreAssessmentFocus
                }) : /*#__PURE__*/_jsx(NavMarker, {
                  name: "LESSON_VIEW_CONTENT_MISSING_ERROR",
                  children: /*#__PURE__*/_jsx(ACMissingContentError, {})
                })
              })
            })
          })]
        })
      })]
    })
  });
};
export default LessonPageRouter;